import { useState, useEffect } from 'react';

const DEFAULT_SETTINGS = {
  type_patient_label: 'élève',
  statuses_enabled: true // Enable note statuses by default
};

export const useSettings = () => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    const loadSettings = () => {
      const savedSettings = localStorage.getItem('confidata_settings');
      if (savedSettings) {
        try {
          const parsed = JSON.parse(savedSettings);
          // Merge with defaults to ensure new settings have default values
          setSettings({ ...DEFAULT_SETTINGS, ...parsed });
        } catch (error) {
          console.error('Erreur lors du chargement des réglages:', error);
        }
      }
    };

    loadSettings();

    // Listen for storage changes
    const handleStorageChange = (e) => {
      if (e.key === 'confidata_settings') {
        loadSettings();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events for same-tab updates
    const handleSettingsUpdate = () => {
      loadSettings();
    };
    
    window.addEventListener('settings-updated', handleSettingsUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('settings-updated', handleSettingsUpdate);
    };
  }, []);

  const updateSettings = (newSettings) => {
    const updatedSettings = { ...settings, ...newSettings };
    setSettings(updatedSettings);
    localStorage.setItem('confidata_settings', JSON.stringify(updatedSettings));
    
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new CustomEvent('settings-updated'));
  };

  const getPatientLabel = () => {
    return settings.type_patient_label || 'élève';
  };

  const getPatientLabelPlural = () => {
    const label = getPatientLabel();
    // Simple pluralization for French
    switch(label) {
      case 'élève': return 'élèves';
      case 'patient': return 'patients';
      case 'client': return 'clients';
      case 'bénéficiaire': return 'bénéficiaires';
      case 'apprenant': return 'apprenants';
      default: return label + 's';
    }
  };

  const getPatientLabelCapitalized = () => {
    const label = getPatientLabel();
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  const getPatientLabelPluralCapitalized = () => {
    const label = getPatientLabelPlural();
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  const getNewPatientLabel = () => {
    const label = getPatientLabel();
    // Règles grammaticales françaises pour "nouveau/nouvel/nouvelle"
    switch(label) {
      case 'élève': return 'Nouvel élève'; // masculin + voyelle = nouvel
      case 'patient': return 'Nouveau patient'; // masculin + consonne = nouveau
      case 'client': return 'Nouveau client'; // masculin + consonne = nouveau
      case 'bénéficiaire': return 'Nouveau bénéficiaire'; // masculin + consonne = nouveau
      case 'apprenant': return 'Nouvel apprenant'; // masculin + voyelle = nouvel
      default: return `Nouveau ${label}`;
    }
  };

  const getCreatePatientLabel = () => {
    const label = getPatientLabel();
    // Pour "Créer le/la..."
    switch(label) {
      case 'élève': return `Créer l'${label}`; // l' devant voyelle
      case 'patient': return `Créer le ${label}`; // le devant consonne masculine
      case 'client': return `Créer le ${label}`; // le devant consonne masculine
      case 'bénéficiaire': return `Créer le ${label}`; // le devant consonne masculine
      case 'apprenant': return `Créer l'${label}`; // l' devant voyelle
      default: return `Créer le ${label}`;
    }
  };

  const getPatientArticle = () => {
    const label = getPatientLabel();
    // Article "ce" ou "cet" selon si le mot commence par une voyelle
    switch(label) {
      case 'élève': return 'cet'; // voyelle
      case 'patient': return 'ce'; // consonne
      case 'client': return 'ce'; // consonne
      case 'bénéficiaire': return 'ce'; // consonne
      case 'apprenant': return 'cet'; // voyelle
      default: return 'ce';
    }
  };

  const getPatientSlug = () => {
    const label = getPatientLabelPlural();
    // Convert to URL-friendly slug
    return label
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  };

  const areStatusesEnabled = () => {
    // Default to true if not explicitly set to false
    return settings.statuses_enabled === true || settings.statuses_enabled === undefined;
  };

  return {
    settings,
    updateSettings,
    getPatientLabel,
    getPatientLabelPlural,
    getPatientLabelCapitalized,
    getPatientLabelPluralCapitalized,
    getNewPatientLabel,
    getCreatePatientLabel,
    getPatientArticle,
    getPatientSlug,
    areStatusesEnabled
  };
};